/**
 * 模型管理 Tab — 从 AdminPanel 中提取
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PencilSquareIcon, TrashIcon, ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { modelConfigApi, systemConfigApi, getErrorMessage } from '../services/api';
import { ModelConfigItem } from '../types';
import { useClickOutside } from '../hooks/useClickOutside';

function ModelsTab() {
  const [configs, setConfigs] = useState<ModelConfigItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({
    name: '', base_url: '', api_key: '', model: '',
    is_default: false, is_shared: true, is_active: true, sort_order: 0, points_ratio: 1.00,
    temperature: 0.70, max_context_tokens: 1048576,
  });

  const [fetchingModels, setFetchingModels] = useState(false);
  const [fetchModelsMsg, setFetchModelsMsg] = useState<string | null>(null);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [includeAllModels, setIncludeAllModels] = useState(false);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const modelInputRef = useRef<HTMLInputElement>(null);

  useClickOutside([modelDropdownRef, modelInputRef], () => setShowModelDropdown(false), showModelDropdown);

  const filteredModels = availableModels.filter((m) =>
    m.toLowerCase().includes(form.model.toLowerCase())
  );

  const loadConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await modelConfigApi.adminList();
      setConfigs(res.data.configs);
    } catch { /* 静默 */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleFetchModels = async () => {
    if (!form.api_key.trim()) {
      setFetchModelsMsg('请先填写 API Key');
      setTimeout(() => setFetchModelsMsg(null), 3000);
      return;
    }
    setFetchingModels(true);
    setFetchModelsMsg(null);
    try {
      const res = await systemConfigApi.fetchModels({ base_url: form.base_url, api_key: form.api_key, model_hint: form.model, include_all: includeAllModels });
      if (res.data.success) {
        setAvailableModels(res.data.models);
        setFetchModelsMsg(res.data.message);
        if (res.data.models.length > 0) setShowModelDropdown(true);
      } else {
        setFetchModelsMsg(res.data.message || '获取失败');
      }
      setTimeout(() => setFetchModelsMsg(null), 5000);
    } catch (err) {
      setFetchModelsMsg(getErrorMessage(err, '获取模型列表失败'));
      setTimeout(() => setFetchModelsMsg(null), 5000);
    } finally { setFetchingModels(false); }
  };

  const openCreateForm = () => {
    setEditingId(null);
    setForm({ name: '', base_url: '', api_key: '', model: '', is_default: false, is_shared: true, is_active: true, sort_order: 0, points_ratio: 1.00, temperature: 0.70, max_context_tokens: 1048576 });
    setAvailableModels([]);
    setFetchModelsMsg(null);
    setShowModelDropdown(false);
    setMsg(null);
    setShowForm(true);
  };

  const openEditForm = (cfg: ModelConfigItem) => {
    setEditingId(cfg.id);
    setForm({ name: cfg.name, base_url: cfg.base_url, api_key: cfg.api_key, model: cfg.model, is_default: cfg.is_default, is_shared: cfg.is_shared, is_active: cfg.is_active, sort_order: cfg.sort_order, points_ratio: cfg.points_ratio ?? 1.00, temperature: cfg.temperature ?? 0.70, max_context_tokens: cfg.max_context_tokens ?? 1048576 });
    setAvailableModels([]);
    setFetchModelsMsg(null);
    setShowModelDropdown(false);
    setMsg(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.base_url.trim() || !form.api_key.trim() || !form.model.trim()) {
      setMsg({ type: 'error', text: '请填写所有必填字段' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      if (editingId) {
        await modelConfigApi.adminUpdate(editingId, form);
        setMsg({ type: 'success', text: '模型配置已更新' });
      } else {
        await modelConfigApi.adminCreate(form);
        setMsg({ type: 'success', text: '模型配置已创建' });
      }
      setShowForm(false);
      await loadConfigs();
    } catch (err) {
      setMsg({ type: 'error', text: getErrorMessage(err, '保存失败') });
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const handleDelete = async (cfg: ModelConfigItem) => {
    if (!window.confirm(`确定要删除模型配置「${cfg.name}」吗？`)) return;
    try { await modelConfigApi.adminDelete(cfg.id); await loadConfigs(); }
    catch (err) { alert(getErrorMessage(err, '删除失败')); }
  };

  const renderStatusMsg = () => {
    if (!msg) return null;
    return (
      <div className={`mb-4 rounded-lg border px-4 py-2 text-sm ${
        msg.type === 'success' ? 'border-green-200 bg-green-50 text-green-700' : 'border-red-200 bg-red-50 text-red-700'
      }`}>{msg.text}</div>
    );
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">大模型配置管理</h3>
          <p className="text-xs text-gray-500 mt-0.5">配置多个大模型供用户选择，设为默认的配置将作为新用户的默认模型。</p>
        </div>
        <button onClick={openCreateForm} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors">
          + 新增配置
        </button>
      </div>

      {renderStatusMsg()}

      {loading ? (
        <div className="text-center py-12 text-sm text-gray-400">加载中...</div>
      ) : (
        <div className="space-y-2">
          {configs.map((cfg) => (
            <div key={cfg.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{cfg.name}</span>
                  {cfg.is_default && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">默认</span>}
                  {!cfg.is_shared && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-700">未共享</span>}
                  {!cfg.is_active && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">已禁用</span>}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">{cfg.model} · {cfg.base_url} · M={cfg.points_ratio ?? 1.00} · T={cfg.temperature ?? 0.70} · CTX={cfg.max_context_tokens ?? 1048576}</div>
              </div>
              <div className="flex items-center gap-1 ml-4">
                <button onClick={() => openEditForm(cfg)} className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors" title="编辑">
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(cfg)} className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="删除">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {configs.length === 0 && <div className="text-center py-12 text-sm text-gray-400">暂无模型配置，点击"新增配置"创建</div>}
        </div>
      )}

      {/* 新增/编辑弹窗 */}
      {showForm && (
        <div className="absolute inset-0 flex items-start justify-center pt-10 z-30">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{editingId ? '编辑模型配置' : '新增模型配置'}</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">配置名称 *</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" placeholder="如 DeepSeek、OpenAI" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Base URL *</label>
                <input type="text" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" placeholder="https://api.deepseek.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key *</label>
                <input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                  className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" placeholder="sk-..." />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">模型名称 *</label>
                <div className="flex gap-2">
                  <div className="relative flex-1" ref={modelDropdownRef}>
                    <input ref={modelInputRef} type="text" value={form.model}
                      onChange={(e) => { setForm({ ...form, model: e.target.value }); if (!showModelDropdown && availableModels.length > 0) setShowModelDropdown(true); }}
                      onFocus={() => { if (availableModels.length > 0) setShowModelDropdown(true); }}
                      className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border pr-8" placeholder="deepseek-chat" />
                    {availableModels.length > 0 && (
                      <button type="button" onClick={() => setShowModelDropdown(!showModelDropdown)} className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                    )}
                    {showModelDropdown && filteredModels.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredModels.map((m) => (
                          <button key={m} type="button" onClick={() => { setForm({ ...form, model: m }); setShowModelDropdown(false); }}
                            className={`block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 transition-colors ${m === form.model ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}>
                            {m}
                          </button>
                        ))}
                      </div>
                    )}
                    {showModelDropdown && form.model && filteredModels.length === 0 && availableModels.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-sm text-gray-400">无匹配模型，当前输入将作为自定义模型名称</div>
                    )}
                  </div>
                  <button type="button" onClick={handleFetchModels} disabled={fetchingModels || !form.api_key.trim()}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap" title="从 API 获取可用模型列表">
                    <ArrowPathIcon className={`w-4 h-4 mr-1 ${fetchingModels ? 'animate-spin' : ''}`} />
                    {fetchingModels ? '获取中...' : '获取模型'}
                  </button>
                  <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer ml-1">
                    <input
                      type="checkbox"
                      checked={includeAllModels}
                      onChange={(e) => setIncludeAllModels(e.target.checked)}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    显示全部
                  </label>
                </div>
                {fetchModelsMsg && (
                  <p className={`mt-1 text-xs ${fetchModelsMsg.includes('失败') || fetchModelsMsg.includes('请先') ? 'text-red-500' : 'text-green-600'}`}>{fetchModelsMsg}</p>
                )}
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">设为默认</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_shared} onChange={(e) => setForm({ ...form, is_shared: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">共享</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">启用</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">排序序号</label>
                <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })}
                  className="block w-24 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">积分转换系数 M <span className="text-xs text-gray-400">（消耗积分 = tokens × M）</span></label>
                <input type="number" step="0.01" min="0.01" value={form.points_ratio} onChange={(e) => setForm({ ...form, points_ratio: parseFloat(e.target.value) || 1.00 })}
                  className="block w-32 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" />
                <p className="mt-1 text-xs text-gray-400">如 DeepSeek 设为 1.00、GPT-4o 设为 15.00</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">LLM 生成温度 <span className="text-xs text-gray-400">（0.0 - 2.0）</span></label>
                <input type="number" step="0.01" min="0" max="2" value={form.temperature} onChange={(e) => setForm({ ...form, temperature: parseFloat(e.target.value) || 0.70 })}
                  className="block w-32 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" />
                <p className="mt-1 text-xs text-gray-400">控制生成随机性，越大越发散；一般 0.0 - 1.0</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">上下文最大 tokens</label>
                <input type="number" step="1" min="1" value={form.max_context_tokens} onChange={(e) => setForm({ ...form, max_context_tokens: parseInt(e.target.value, 10) || 1048576 })}
                  className="block w-32 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm px-3 py-2 border" />
                <p className="mt-1 text-xs text-gray-400">单次请求送入模型上下文的最大 token 数</p>
              </div>
              {msg && (
                <div className={`px-3 py-2 rounded-md text-sm ${msg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>{msg.text}</div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm font-medium rounded-md text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors">取消</button>
              <button onClick={handleSave} disabled={saving} className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 transition-colors">
                {saving ? '保存中...' : '确认保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ModelsTab;
